/**
 * Generic preview navigator for communication channels.
 *
 * All channel-specific behaviour is driven by data-* attributes on the host element:
 *
 *   data-comm-id              - saved communication ID (empty/NaN = creation mode)
 *   data-use-form             - "true" to force creation mode even with a commId
 *   data-recipients-selector  - CSS class selector for the hidden <template> holding recipient spans
 *   data-post-url             - POST endpoint path for creation-mode preview (relative to NEXT_URL)
 *   data-get-url              - GET  endpoint path for saved-comm preview  (relative to NEXT_URL)
 *   data-initial-*            - initial raw values, one per field (e.g. data-initial-body, data-initial-subject)
 *
 * A child <script type="application/json" class="js-form-fields"> provides a JSON array
 * describing how to read live form values in creation mode. Each entry:
 *   { name, cardRef, selector, type }
 *     name     - field name sent to the server (e.g. "body", "subject")
 *     cardRef  - x-ref of the channel card in the form (e.g. "emailCard")
 *     selector - CSS selector inside the card to find the input element
 *     type     - "value" (read .value) or "tiptap" (read ._tiptapEditor.getHTML())
 */
export default () => ({
    commId: null,
    previewMode: false,
    recipients: [],
    currentIndex: 0,
    loading: false,
    rawFields: {},
    _recipientsSelector: null,
    _postUrl: null,
    _getUrl: null,
    _formFields: [],
    _shadowRoot: null,
    _previewOnDone: null,

    init() {
        const el = this.$el;
        const ds = el.dataset;

        this.commId = parseInt(ds.commId);
        this._shadowRoot = el.getRootNode();

        // Channel-agnostic config from data attributes
        this._recipientsSelector = ds.recipientsSelector;
        this._postUrl = ds.postUrl;
        this._getUrl = ds.getUrl;
        const fieldsScript = el.querySelector('script.js-form-fields');
        this._formFields = fieldsScript ? JSON.parse(fieldsScript.textContent) : [];

        // Initialize raw fields from matching data-initial-* attributes
        for (const field of this._formFields) {
            const attrKey = 'initial' + field.name.charAt(0).toUpperCase() + field.name.slice(1);
            this.rawFields[field.name] = ds[attrKey] || '-';
        }

        // Parse recipients from hidden template
        const tpl = el.querySelector(this._recipientsSelector);
        if (tpl) {
            this.recipients = Array.from(tpl.content.querySelectorAll('span')).map(s => ({
                perId: parseInt(s.dataset.perId),
                csId: parseInt(s.dataset.csId),
                sourceType: s.dataset.sourceType || null,
                sourceId: s.dataset.sourceId ? parseInt(s.dataset.sourceId) : null,
                targetPerId: s.dataset.targetPerId ? parseInt(s.dataset.targetPerId) : null,
                label: s.dataset.label
            }));
        }

        if (this.isCreationMode()) {
            this._shadowRoot.addEventListener('kibe:summary-tab-click', () => {
                // Skip if this channel card is hidden (x-show=false on parent)
                if (!this.$el.offsetParent) return;
                this.previewMode = true;
                this._refreshRaw();
                this.loadPreview();
            });
        }
    },

    _refreshRaw() {
        const values = this._getFormValues();
        for (const [key, val] of Object.entries(values)) {
            this.rawFields[key] = val || '-';
        }
    },

    isCreationMode() {
        return isNaN(this.commId) || this.$el.dataset.useForm === 'true';
    },

    _getFormValues() {
        const root = this._shadowRoot;
        const result = {};
        for (const field of this._formFields) {
            const container = field.cardRef
                ? root.querySelector(`[x-ref="${field.cardRef}"]`)
                : root;
            if (!container) { result[field.name] = ''; continue; }
            const el = container.querySelector(field.selector);
            if (!el) { result[field.name] = ''; continue; }
            result[field.name] = field.type === 'tiptap' && el._tiptapEditor
                ? el._tiptapEditor.getHTML()
                : (el.value || '');
        }
        return result;
    },

    onModeChange() {
        if (this.previewMode && this.recipients.length > 0) {
            this.loadPreview();
        }
    },

    prev() {
        if (this.currentIndex > 0) {
            this.currentIndex--;
            this.loadPreview();
        }
    },

    next() {
        if (this.currentIndex < this.recipients.length - 1) {
            this.currentIndex++;
            this.loadPreview();
        }
    },

    recipientLabel() {
        if (this.recipients.length === 0) return '';
        return `${this.recipients[this.currentIndex].label} (${this.currentIndex + 1}/${this.recipients.length})`;
    },

    loadPreview() {
        const r = this.recipients[this.currentIndex];
        if (!r) return;

        const target = this.$refs.previewContent;

        if (this._previewOnDone) {
            this._shadowRoot.removeEventListener('htmx:afterSwap', this._previewOnDone);
            this._shadowRoot.removeEventListener('htmx:responseError', this._previewOnDone);
            this._shadowRoot.removeEventListener('htmx:sendError', this._previewOnDone);
        }

        this.loading = true;

        this._previewOnDone = () => {
            this.loading = false;
            this._shadowRoot.removeEventListener('htmx:afterSwap', this._previewOnDone);
            this._shadowRoot.removeEventListener('htmx:responseError', this._previewOnDone);
            this._shadowRoot.removeEventListener('htmx:sendError', this._previewOnDone);
            this._previewOnDone = null;
        };
        this._shadowRoot.addEventListener('htmx:afterSwap', this._previewOnDone, { once: true });
        this._shadowRoot.addEventListener('htmx:responseError', this._previewOnDone, { once: true });
        this._shadowRoot.addEventListener('htmx:sendError', this._previewOnDone, { once: true });

        if (this.isCreationMode()) {
            const formValues = this._getFormValues();
            const values = { perId: r.perId, sourceType: r.sourceType, sourceId: r.sourceId, ...formValues };
            if (r.targetPerId != null) values.targetPerId = r.targetPerId;

            htmx.ajax('POST', window.NEXT_URL + this._postUrl, {
                source: target,
                target,
                swap: 'innerHTML',
                headers: { 'HX-Target': 'previewContent' },
                values
            });
        } else {
            const params = new URLSearchParams({ commId: this.commId, perId: r.perId, csId: r.csId });
            if (r.targetPerId != null) params.set('targetPerId', r.targetPerId);

            htmx.ajax('GET', window.NEXT_URL + this._getUrl + '?' + params.toString(), {
                target,
                swap: 'innerHTML',
                headers: { 'HX-Target': 'previewContent' }
            });
        }
    }
});
